/**
 * „B2B & Provision"-Bereich in „Meine Verkäufe" (Jul-2026, User-Wunsch):
 * eigene B2B-Käufe & Vermittlungen des Verkäufers inkl. Provision.
 * Datenquelle: GET /api/dealer/b2b/my-deals → { items, stats }.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Handshake, ShoppingCart, Euro } from "lucide-react";
import api from "../../lib/api";

const eur = (v) => (v == null ? "—" : Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const dt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } };

const STATUS_LABEL = {
    open: ["Offen", "bg-amber-100 text-amber-800"],
    accepted: ["Angenommen", "bg-blue-100 text-blue-700"],
    completed: ["Abgeschlossen", "bg-emerald-100 text-emerald-700"],
    rejected: ["Abgelehnt", "bg-red-100 text-red-700"],
    withdrawn: ["Zurückgezogen", "bg-gray-100 text-gray-600"],
};

export const MyB2BDealsSection = () => {
    const [data, setData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/dealer/b2b/my-deals").then((r) => setData(r.data)).catch(() => setData({ items: [], stats: {} }));
    }, []);

    if (!data) return <div className="text-center py-8 text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
    const s = data.stats || {};
    return (
        <div data-testid="my-b2b-deals-section">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                <div className="border border-gray-200 rounded-sm p-3" data-testid="b2b-stat-kauf">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> B2B-Käufe</div>
                    <div className="text-xl font-black font-mono-num">{s.kauf_count || 0}</div>
                    <div className="text-[10px] text-gray-400">Volumen {eur(s.kauf_volume || 0)}</div>
                </div>
                <div className="border border-gray-200 rounded-sm p-3" data-testid="b2b-stat-vermittlung">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1"><Handshake className="w-3 h-3" /> Vermittlungen</div>
                    <div className="text-xl font-black font-mono-num">{s.vermittlung_count || 0}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3" data-testid="b2b-stat-provision">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1"><Euro className="w-3 h-3" /> Provision erhalten</div>
                    <div className="text-xl font-black font-mono-num text-emerald-700">{eur(s.provision_sum || 0)}</div>
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="b2b-stat-provision-pending">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1"><Euro className="w-3 h-3" /> Provision offen</div>
                    <div className="text-xl font-black font-mono-num text-amber-700">{eur(s.provision_pending || 0)}</div>
                </div>
            </div>
            {(data.items || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm" data-testid="b2b-deals-empty">
                    Noch keine B2B-Käufe oder Vermittlungen — starte über die Disposition („Verkauf / Vermittlung" an fremden Fahrzeugen).
                </div>
            ) : (
                <ul className="space-y-1.5" data-testid="b2b-deals-list">
                    {data.items.map((d) => {
                        const [label, cls] = STATUS_LABEL[d.status] || [d.status, "bg-gray-100 text-gray-600"];
                        return (
                            <li key={d.id}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/dealer/b2b-anfragen?deal=${d.id}&dir=outgoing`)}
                                    className="w-full text-left border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827] transition-colors"
                                    data-testid={`b2b-deal-${d.id}`}
                                >
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-[9px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 ${d.mode === "vermittlung" ? "bg-indigo-100 text-indigo-700" : "bg-blue-100 text-blue-700"}`}>
                                            {d.mode === "vermittlung" ? "Vermittlung" : d.binding ? "Sofortkauf" : "Kauf"}
                                        </span>
                                        <span className="font-bold text-sm truncate">{d.vehicle_label || "Fahrzeug"}</span>
                                        <span className={`text-[9px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 ${cls}`}>{label}</span>
                                        <span className="ml-auto text-[11px] text-gray-400 font-mono-num">{dt(d.created_at)}</span>
                                    </div>
                                    <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                        <span>Verkäufer-Händler: {d.to_dealer_name || "—"}</span>
                                        {d.price_b2b != null && <span>Preis: <b className="font-mono-num">{eur(d.price_b2b)}</b></span>}
                                        {d.mode === "vermittlung" && <span className="text-emerald-700">Provision: <b className="font-mono-num">{eur(d.provision_eur)}</b></span>}
                                        {d.customer && <span>Endkunde: {d.customer}</span>}
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default MyB2BDealsSection;
